import React, { useState, useEffect } from 'react';
import { collection, addDoc, doc, updateDoc, query, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { getAllBrands } from '../../lib/brandService';
import { X, Save, Package, Plus, Trash2, Tag, Check, Cog, Palette } from 'lucide-react';

const BrandForm = ({ isOpen, onClose, onSuccess, editingBrand = null }) => {
  const { userRole } = useAuth();
  const [formData, setFormData] = useState({
    nombre: '',
    tiposEquipoIds: [],
    color: '#3B82F6' // Default blue color
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [tiposEquipo, setTiposEquipo] = useState([]);
  const [tiposLoading, setTiposLoading] = useState(false);

  // Load equipment types from Firestore when form opens
  useEffect(() => {
    if (isOpen) {
      fetchTiposEquipo();
    }
  }, [isOpen]);

  const fetchTiposEquipo = async () => {
    try {
      setTiposLoading(true);
      const q = query(collection(db, 'tipos_equipo'), orderBy('nombre'));
      const querySnapshot = await getDocs(q);
      const tiposList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setTiposEquipo(tiposList);
    } catch (error) {
      console.log('Tipos_equipo collection may not exist yet, starting with empty list');
      setTiposEquipo([]);
    } finally {
      setTiposLoading(false);
    }
  };

  // Populate form when editing
  useEffect(() => {
    if (editingBrand) {
      setFormData({
        nombre: editingBrand.nombre || '',
        tiposEquipoIds: editingBrand.tiposEquipoIds || [],
        color: editingBrand.color || '#3B82F6'
      });
    } else {
      // Reset form for new brand
      setFormData({
        nombre: '',
        tiposEquipoIds: [],
        color: '#3B82F6'
      });
    }
    setErrors({});
  }, [editingBrand, isOpen]);

  const validateForm = async () => {
    const newErrors = {};

    // Validate brand name
    if (!formData.nombre.trim()) {
      newErrors.nombre = 'El nombre de la marca es requerido';
    } else {
      // Check for duplicate brand names
      try {
        const brandsQuery = query(collection(db, 'marcas'));
        const brandsSnapshot = await getDocs(brandsQuery);
        
        const duplicateFound = brandsSnapshot.docs.some(doc => {
          const existingBrand = doc.data();
          const isDifferentBrand = !editingBrand || doc.id !== editingBrand.id;
          return isDifferentBrand && 
                 existingBrand.nombre?.toLowerCase() === formData.nombre.trim().toLowerCase();
        });

        if (duplicateFound) {
          newErrors.nombre = 'Ya existe una marca con este nombre';
        }
      } catch (error) {
        // Handle case where collection doesn't exist yet - this is normal for first-time use
        console.log('Marcas collection may not exist yet, skipping duplicate check');
        // Don't set an error since this is expected behavior when no brands exist yet
      }
    }

    // Validate equipment types
    if (formData.tiposEquipoIds.length === 0) {
      newErrors.tiposEquipoIds = 'Debe seleccionar al menos un tipo de equipo';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleTipoToggle = (tipoId) => {
    setFormData(prev => {
      const isSelected = prev.tiposEquipoIds.includes(tipoId);
      const updatedTiposIds = isSelected 
        ? prev.tiposEquipoIds.filter(id => id !== tipoId)
        : [...prev.tiposEquipoIds, tipoId];
      
      return {
        ...prev,
        tiposEquipoIds: updatedTiposIds
      };
    });

    // Clear equipment types error when user makes selection
    if (errors.tiposEquipoIds) {
      setErrors(prev => ({
        ...prev,
        tiposEquipoIds: ''
      }));
    }
  };

  const isTipoSelected = (tipoId) => {
    return formData.tiposEquipoIds.includes(tipoId);
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const isValid = await validateForm();
    if (!isValid) {
      return;
    }

    // Check permissions
    if (!userRole || !['admin', 'shopkeeper'].includes(userRole)) {
      setSubmitError(`No tienes permisos para ${editingBrand ? 'editar' : 'crear'} marcas. Tu rol actual es: ${userRole || 'sin rol'}`);
      return;
    }

    setLoading(true);

    try {
      // Clean up data
      const cleanedData = {
        nombre: formData.nombre.trim(),
        tiposEquipoIds: formData.tiposEquipoIds,
        color: formData.color,
        updatedAt: new Date()
      };

      if (editingBrand) {
        // Update existing brand
        await updateDoc(doc(db, 'marcas', editingBrand.id), cleanedData);
      } else {
        // Create new brand
        cleanedData.createdAt = new Date();
        await addDoc(collection(db, 'marcas'), cleanedData);
      }
      
      // Force refresh brand cache to ensure updated data is available immediately
      try {
        await getAllBrands(true);
        console.log('Brand cache refreshed after update');
      } catch (cacheError) {
        console.error('Error refreshing brand cache:', cacheError);
        // Continue even if cache refresh fails
      }
      
      // Reset form and close
      resetForm();
      
      if (onSuccess) {
        onSuccess();
      }
      
      onClose();
    } catch (error) {
      console.error('Error saving brand:', error);
      setSubmitError('Error al guardar la marca. Por favor intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      nombre: '',
      tiposEquipoIds: [],
      color: '#3B82F6'
    });
    setErrors({});
    setSubmitError('');
  };

  const handleClose = () => {
    if (!loading) {
      resetForm();
      onClose();
    }
  };

  const getSelectedTiposCount = () => {
    return formData.tiposEquipoIds.length;
  };

  const getSelectedTiposNames = () => {
    return formData.tiposEquipoIds.map(id => {
      const tipo = tiposEquipo.find(t => t.id === id);
      return tipo ? tipo.nombre : 'Tipo desconocido';
    }).join(', ');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Package className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {editingBrand ? 'Editar Marca' : 'Nueva Marca'}
              </h2>
            </div>
            <button
              onClick={handleClose}
              disabled={loading}
              className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-6">
            {/* Brand Name */}
            <div>
              <label htmlFor="nombre" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Nombre de la Marca *
              </label>
              <input
                type="text"
                id="nombre"
                value={formData.nombre}
                onChange={(e) => handleInputChange('nombre', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 ${
                  errors.nombre ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder="Ej: Grundfos, Franklin Electric, Pedrollo..."
                disabled={loading}
              />
              {errors.nombre && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.nombre}</p>
              )}
            </div>

            {/* Brand Color */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Color de la Marca *
              </label>
              <div className="space-y-4">
                {/* Color Preview */}
                <div className="flex items-center space-x-3">
                  <div 
                    className="w-8 h-8 rounded-full border-2 border-gray-300 dark:border-gray-600 flex items-center justify-center"
                    style={{ backgroundColor: formData.color }}
                  >
                    <Package className="h-4 w-4 text-white" />
                  </div>
                  <span 
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium text-white"
                    style={{ backgroundColor: formData.color }}
                  >
                    <Package className="h-4 w-4 mr-1" />
                    {formData.nombre || 'Nombre de Marca'}
                  </span>
                </div>
                
                {/* Predefined Colors */}
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Colores predefinidos:</p>
                  <div className="grid grid-cols-8 gap-2">
                    {[
                      '#EF4444', // Red
                      '#F97316', // Orange
                      '#EAB308', // Yellow
                      '#22C55E', // Green
                      '#10B981', // Emerald
                      '#06B6D4', // Cyan
                      '#3B82F6', // Blue
                      '#6366F1', // Indigo
                      '#8B5CF6', // Violet
                      '#A855F7', // Purple
                      '#EC4899', // Pink
                      '#F43F5E', // Rose
                      '#64748B', // Slate
                      '#6B7280', // Gray
                      '#374151', // Gray-700
                      '#1F2937'  // Gray-800
                    ].map((color) => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => handleInputChange('color', color)}
                        className={`w-8 h-8 rounded-full border-2 hover:scale-110 transition-transform ${
                          formData.color === color ? 'border-gray-800 dark:border-gray-200 shadow-lg' : 'border-gray-300 dark:border-gray-600'
                        }`}
                        style={{ backgroundColor: color }}
                        title={color}
                        disabled={loading}
                      />
                    ))}
                  </div>
                </div>
                
                {/* Custom Color Input */}
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">O elige un color personalizado:</p>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      value={formData.color}
                      onChange={(e) => handleInputChange('color', e.target.value)}
                      className="w-12 h-8 border border-gray-300 dark:border-gray-600 rounded cursor-pointer disabled:opacity-50 dark:bg-gray-700"
                      disabled={loading}
                    />
                    <input
                      type="text"
                      value={formData.color}
                      onChange={(e) => handleInputChange('color', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 text-sm font-mono bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                      placeholder="#3B82F6"
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Equipment Types */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Tipos de Equipo *
                </label>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {getSelectedTiposCount()} seleccionado{getSelectedTiposCount() !== 1 ? 's' : ''}
                </div>
              </div>
              
              {errors.tiposEquipoIds && (
                <div className="mb-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-3 py-2 rounded-md text-sm">
                  {errors.tiposEquipoIds}
                </div>
              )}
              
              {/* Selected types summary */}
              {getSelectedTiposCount() > 0 && (
                <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
                  <p className="text-sm text-blue-800 dark:text-blue-400">
                    <strong>Tipos seleccionados:</strong> {getSelectedTiposNames()}
                  </p>
                </div>
              )}
              
              {tiposLoading ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  Cargando tipos de equipo...
                </div>
              ) : tiposEquipo.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <Cog className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                  <p className="text-lg font-medium text-gray-900 dark:text-white mb-2">No hay tipos de equipo</p>
                  <p>Primero debe crear tipos de equipo en la gestión de tipos de equipo.</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-md">
                  {tiposEquipo.map((tipo) => (
                    <div key={tipo.id} className="flex items-center p-3 hover:bg-gray-50 dark:hover:bg-gray-700">
                      <input
                        type="checkbox"
                        id={`tipo-${tipo.id}`}
                        checked={isTipoSelected(tipo.id)}
                        onChange={() => handleTipoToggle(tipo.id)}
                        disabled={loading}
                        className="h-4 w-4 text-blue-600 dark:text-blue-400 focus:ring-blue-500 dark:focus:ring-blue-400 border-gray-300 dark:border-gray-600 rounded disabled:opacity-50 dark:bg-gray-600"
                      />
                      <label 
                        htmlFor={`tipo-${tipo.id}`}
                        className="ml-3 flex-1 cursor-pointer"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {tipo.nombre}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              Creado: {new Date(tipo.createdAt?.toDate ? tipo.createdAt.toDate() : tipo.createdAt).toLocaleDateString('es-ES')}
                            </p>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-400">
                              {tipo.specsIds?.length || tipo.especificaciones?.length || 0} specs
                            </span>
                            {isTipoSelected(tipo.id) && (
                              <Check className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                            )}
                          </div>
                        </div>
                      </label>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md">
                <p className="text-sm text-blue-800 dark:text-blue-400">
                  <strong>Nota:</strong> Los tipos de equipo seleccionados definen qué categorías de equipos 
                  puede producir esta marca. Solo se mostrarán tipos que ya han sido creados en la gestión 
                  de tipos de equipo.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-8 flex justify-end space-x-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 dark:bg-blue-700 text-white rounded-md hover:bg-blue-700 dark:hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              <span>{loading ? 'Guardando...' : (editingBrand ? 'Actualizar Marca' : 'Guardar Marca')}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BrandForm;